/**
 * Tree Character Component (Pepe the Algarrobo)
 * Interactive tree that shows discovered friends and breathes
 * Uses Pepe-habla SVG files for different poses
 */

import { motion, AnimatePresence } from 'framer-motion';

type BreathingPhase = 'inhale' | 'exhale' | 'idle';

interface Discovery {
  x: number;
  y: number;
  label: string;
  icon?: string;
  sound?: string;
}

interface TreeProps {
  discovered: Set<string>;
  discoveries: Record<string, Discovery>;
  onDiscovery?: (key: string) => void;
  breathingPhase?: BreathingPhase;
  showDiscoveryHints?: boolean;
  isComplete?: boolean;
  isInitialBreathing?: boolean;
}

export function Tree({
  discovered,
  discoveries,
  onDiscovery,
  breathingPhase = 'idle',
  showDiscoveryHints = true,
  isComplete = false,
  isInitialBreathing = false
}: TreeProps) {

  // Choose the right Pepe pose based on state
  const getPepeImage = () => {
    // Initial breathing pose (when instructions are showing)
    if (isInitialBreathing) {
      return '/Pepe-respira-1-neutral.svg?v=3'; // Initial neutral pose
    }

    // During breathing phase, use specific breathing SVGs
    if (breathingPhase === 'inhale') {
      return '/Pepe-respira-2-inhale.svg?v=3'; // Inhale pose
    } else if (breathingPhase === 'exhale') {
      return '/Pepe-respira-3-exhale.svg?v=3'; // Exhale pose
    }

    // For discovery phase, cycle through habla SVGs based on discoveries
    if (!isComplete && breathingPhase === 'idle') {
      const discoveryCount = discovered.size;
      const totalDiscoveries = Object.keys(discoveries).length;

      // Cycle through the 4 habla poses based on discovery progress
      if (discoveryCount === 0) {
        return '/Pepe-habla_1.svg?v=2'; // Starting pose
      } else if (discoveryCount < totalDiscoveries / 3) {
        return '/Pepe-habla_2.svg?v=2'; // Early discoveries
      } else if (discoveryCount < (totalDiscoveries * 2) / 3) {
        return '/Pepe-habla_3.svg?v=2'; // Mid discoveries
      } else if (discoveryCount < totalDiscoveries) {
        return '/Pepe-habla_4.svg?v=2'; // Almost done!
      }
    }

    // Completion phase
    if (isComplete) {
      return '/Pepe-habla_4.svg?v=2'; // Happy/Complete pose
    }

    return '/Pepe-habla_1.svg?v=2'; // Default fallback
  };

  // Tree animation based on breathing phase
  const getTreeAnimation = () => {
    if (breathingPhase === 'inhale') {
      return {
        scale: 1.2,
        transition: { duration: 4, ease: 'easeInOut' }
      };
    } else if (breathingPhase === 'exhale') {
      return {
        scale: 0.9,
        transition: { duration: 4, ease: 'easeInOut' }
      };
    }
    return {
      scale: 1,
      rotate: [0, -2, 2, -2, 2, 0],
      transition: {
        rotate: { duration: 3, repeat: Infinity, ease: 'easeInOut' }
      }
    };
  };

  const handleDiscoveryClick = (key: string) => {
    if (onDiscovery && !discovered.has(key)) {
      onDiscovery(key);
    }
  };

  return (
    <motion.div
      className="absolute inset-0 flex justify-center items-center"
      animate={getTreeAnimation()}
    >
      {/* Pepe Tree SVG - switches between 4 poses with smooth crossfade */}
      <div
        className="relative select-none pepe-tree-container"
        style={{ marginTop: '15%' }}
      >
        <AnimatePresence initial={false}>
          <motion.img
            key={getPepeImage()}
            src={getPepeImage()}
            alt="Pepe the Algarrobo Tree"
            className="pepe-tree-image w-[1900px] h-[2533px] object-contain pepe-tree-mobile"
            draggable={false}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
          />
        </AnimatePresence>

        {/* Discovery points overlay on the tree */}
        <svg
          viewBox="0 0 100 100"
          className="absolute inset-0 w-full h-full"
          style={{ pointerEvents: 'none' }}
        >
          {breathingPhase === 'idle' && Object.entries(discoveries).map(([key, item]) => (
            <g key={key} style={{ pointerEvents: 'auto' }}>
              {/* Clickable discovery circle */}
              <circle
                cx={item.x}
                cy={item.y}
                r="5"
                fill={discovered.has(key) ? "rgba(255,255,255,0.3)" : "transparent"}
                stroke={discovered.has(key) ? "none" : showDiscoveryHints ? "rgba(235, 115, 217, 0.8)" : "none"}
                strokeWidth="0.8"
                className="cursor-pointer hover:fill-purple-300/50 transition-all discovery-circle-mobile"
                onClick={() => handleDiscoveryClick(key)}
                style={{
                  animation: discovered.has(key) ? 'none' : 'pulse 2s infinite'
                }}
              />

              {/* Discovered item display - only show during discovery phase (idle) */}
              {discovered.has(key) && (
                <g className="animate-pop">
                  <text
                    x={item.x}
                    y={item.y}
                    fontSize="6"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="discovery-icon-mobile"
                  >
                    {item.icon || item.sound}
                  </text>
                  <text
                    x={item.x}
                    y={item.y + 5}
                    fontSize="2.5"
                    textAnchor="middle"
                    fill="#fff"
                    className="discovery-label-mobile"
                    style={{
                      textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                      fontWeight: 'bold'
                    }}
                  >
                    {item.label}
                  </text>
                </g>
              )}
            </g>
          ))}
        </svg>
      </div>

      {/* CSS animations */}
      <style>{`
        .pepe-tree-container {
          display: inline-block;
          position: relative;
        }
        .pepe-tree-image {
          position: absolute;
          top: 0;
          left: 0;
        }
        .pepe-tree-image:first-child {
          position: relative;
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 0.6;
            stroke: rgba(235, 115, 217, 0.6);
          }
          50% {
            opacity: 1;
            stroke: rgba(235, 115, 217, 1);
          }
        }
        @keyframes pop {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-pop {
          animation: pop 0.4s ease-out;
        }
      `}</style>
    </motion.div>
  );
}
